import { FC } from "react";
import CurrencyIconSVG from "../../../resources/svg/CurrencyIconSVG";
import RatingStarSVG from "../../../resources/svg/RatingStarIconSVG";
import { useTranslation } from "react-i18next";
import {
    getApplicationCreatedTime,
    getWorkUserProfileImage,
    isApplicationAccepted,
    isApplicationDeclined,
    isApplicationResolved,
    isCurrentApplicationOwner,
    isCurrentWorkOwner,
} from "../../../../slices/works-slice";
import { ApplicationDTO } from "../../../../domain/models/dto/ApplicationDTO";
import AcceptCheckMarkSVG from "../../../resources/svg/functional/AcceptCheckMarkSVG";
import DeclineCrossSVG from "../../../resources/svg/functional/DeclineCrossSVG";
import languageServiceFactory from "../../../../services/LanguageServiceImpl";
import { WorkDTO } from "../../../../domain/models/dto/WorkDTO";
import ApplicationManagementButtons from "./ApplicationManagementButtons";
import useScreenSize from "../../../../custom-hooks/base-hooks/useScreenSize";
import { useSelector } from "react-redux";
import AcceptAndDeclineButtons from "../../../resources/components/AcceptAndDeclineButtons";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { getUserFullName } from "../../../../slices/user-slice";
import CurrencyAmount from "../../../resources/components/CurrencyAmount";

interface ApplicationCardProps {
    work: WorkDTO;
    application: ApplicationDTO;
    deleteApplicationCallback: () => void;
    isAcceptOrDeclineAvailable: boolean;
    setAcceptOrDeclineAvailable: (value: boolean) => void;
}

const languageService = languageServiceFactory();

const ApplicationCard: FC<ApplicationCardProps> = (props) => {
    const { upTo650, moreOrEqual650 } = useScreenSize();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isResolved = isApplicationResolved(props.application);
    const isAccepted = isApplicationAccepted(props.application);
    const isDeclined = isApplicationDeclined(props.application);
    const isApplicationOwner = useSelector(isCurrentApplicationOwner(props.work, props.application));
    const currentUserIsOwner = useSelector(isCurrentWorkOwner(props.work));

    const fullName = getUserFullName(props.application.user, t);

    return (
        <div className="w-full">
            <div
                className={`relative flex flex-col bg-backgroundInput rounded-[10px] ${
                    isResolved ? "pt-[45px] dmax2:pt-[48px]" : "pt-6 dmax2:pt-5"
                } pb-6 dmax2:pb-5 px-8 dmax2:px-4 max-w-[845px]`}
            >
                {isResolved && (
                    <div
                        className={`absolute flex items-center justify-center ${
                            languageService.isEnglish ? "w-[180px]" : "w-[161px]"
                        } h-[31px] top-0 left-0 rounded-tl-[10px] rounded-br-[40px] ${isAccepted && "bg-accentGreen"} ${
                            isDeclined && "bg-accentRed"
                        }`}
                    >
                        {isAccepted && (
                            <div className="flex items-center justify-center">
                                <AcceptCheckMarkSVG className="text-textWhite w-[9px] h-[9px] mr-2" />
                                <span className="H12 text-textWhite">{t("work.applicationApproved")}</span>
                            </div>
                        )}
                        {isDeclined && (
                            <div className="flex items-center justify-center">
                                <DeclineCrossSVG className="text-textWhite w-[9px] h-[9px] mr-2" />
                                <span className="H12 text-textWhite">{t("work.applicationRejected")}</span>
                            </div>
                        )}
                    </div>
                )}
                {isApplicationOwner && upTo650 && !isResolved && (
                    <ApplicationManagementButtons
                        work={props.work}
                        application={props.application}
                        deleteApplicationCallback={props.deleteApplicationCallback}
                    />
                )}
                <div className="flex justify-between gap-4 w-full">
                    <div className="relative flex justify-between">
                        <div
                            onClick={() => navigate(`/profile/${_.get(props.application, "user.id")}`)}
                            className="relative flex items-center mb-5 tmax4:mb-4 cursor-pointer"
                        >
                            <div className="w-[62px] min-w-[62px] tmax4:w-[46px] tmax4:min-w-[46px] h-[62px] min-h-[62px] tmax4:h-[46px] tmax4:min-h-[46px] mr-5 dmax2:mr-2">
                                <img
                                    src={getWorkUserProfileImage(props.application.user, true)}
                                    alt="user"
                                    className="w-full h-full object-cover rounded-full"
                                />
                            </div>
                            <div className="relative flex flex-col">
                                <p className="H16B tmax4:H14B text-textWhite text-left tmax5:line-clamp-2 tmax4:max-w-[110px] ti7:max-w-[210px]">
                                    {fullName}
                                </p>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <span className="H16B tmax4:H14B text-textWhite mr-1 dmax2:mr-[2px]">
                                            {props.application.user?.avgRating}
                                        </span>
                                        <RatingStarSVG className="w-[21px] dmax2:w-5 mr-2 dmax2:mr-1" />
                                        <span className="H16B tmax4:H14B text-textFaded">
                                            {props.application.user?.reviewsCount}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-start gap-6">
                        {isApplicationOwner && moreOrEqual650 && !isResolved && (
                            <ApplicationManagementButtons
                                work={props.work}
                                application={props.application}
                                deleteApplicationCallback={props.deleteApplicationCallback}
                            />
                        )}
                        <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] text-nowrap w-fit h-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px] mb-5 tmax4:mb-4">
                            <CurrencyIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                            <span>
                                <CurrencyAmount
                                    price={props.application.price}
                                    suffix={t("work.applicationCreation.priceUnits")}
                                />
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col max-w-full h-full mb-5">
                    <p className="H16 tmax4:H14 text-textWhite text-start break-words">{props.application.comment}</p>
                    <div className="H16 tmax4:H14 text-textFaded text-end mt-4">
                        {getApplicationCreatedTime(t, props.application)}
                    </div>
                </div>
            </div>
            {currentUserIsOwner && (
                <AcceptAndDeclineButtons
                    application={props.application}
                    work={props.work}
                    isAcceptOrDeclineAvailable={props.isAcceptOrDeclineAvailable}
                    setAcceptOrDeclineAvailable={props.setAcceptOrDeclineAvailable}
                />
            )}
        </div>
    );
};

export default ApplicationCard;
